import { useEffect, useState, useCallback } from "react";
import { Brain, Plus, Trash2, Pencil, Check, X, Search as SearchIcon, Globe, Lock, Building2, Share2, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller, Spinner, EmptyState, goldBtn } from "@/pages/_shared";

const SCOPE_META = {
  private: { label: "Private", icon: Lock, cls: "text-blue-300 border-blue-400/30" },
  group: { label: "Group", icon: Globe, cls: "text-gold border-gold/30" },
  shared: { label: "Shared", icon: Share2, cls: "text-emerald-300 border-emerald-400/30" },
  company: { label: "Company", icon: Building2, cls: "text-purple-300 border-purple-400/30" },
  unassigned: { label: "Needs review", icon: HelpCircle, cls: "text-amber-300 border-amber-400/30" },
};

export default function Memory() {
  const { companies, activeCompany, activeCompanyId, isGroupView } = useOrg();
  const [mems, setMems] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftScope, setDraftScope] = useState(isGroupView ? "group" : "company");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "a business";

  const load = useCallback(async () => {
    const { data } = await api.get("/memories", { params: q ? { q } : {} });
    setMems(data);
  }, [q]);
  useEffect(() => { load(); }, [load, activeCompanyId]);

  const scopeBody = (scopeVal) => {
    if (scopeVal.startsWith("company:")) return { scope: "company", company_id: scopeVal.split(":")[1] };
    return { scope: scopeVal };
  };

  const add = async () => {
    if (!draft.trim()) return;
    await api.post("/memories", { content: draft.trim(), type: "user", source: "manual", ...scopeBody(draftScope) });
    setDraft(""); setAdding(false); toast.success("Memory saved"); load();
  };
  const toggle = async (m) => { await api.patch(`/memories/${m.id}`, { enabled: !m.enabled }); load(); };
  const saveEdit = async (id) => { await api.patch(`/memories/${id}`, { content: editText }); setEditId(null); load(); };
  const del = async (id) => { await api.delete(`/memories/${id}`); toast.success("Deleted"); load(); };
  const reclassify = async (m, scopeVal) => { await api.patch(`/memories/${m.id}`, scopeBody(scopeVal)); toast.success("Memory reclassified"); load(); };

  if (!mems) return <Spinner />;

  const shown = mems.filter((m) => filter === "all" || m.scope === filter || (!m.scope && filter === "unassigned"));
  const scopeOptions = [
    { value: "private", label: "Private (only me)" },
    { value: "group", label: "Group (whole group)" },
    { value: "shared", label: "Shared across businesses" },
    ...companies.map((c) => ({ value: `company:${c.id}`, label: `Company · ${c.name}` })),
  ];
  const currentScopeVal = (m) => (m.scope === "company" && m.company_id ? `company:${m.company_id}` : (m.scope || "unassigned"));

  return (
    <Scroller>
      <PageHeader title="Memory" subtitle={isGroupView ? "Group & company memory — organised so nothing leaks between businesses" : `Memory for ${activeCompany?.name || "this business"} + your group`} icon={Brain}
        action={<button onClick={() => setAdding((v) => !v)} className={goldBtn} data-testid="memory-add-toggle"><Plus className="h-4 w-4" /> Add Memory</button>} />

      {adding && (
        <div className="mb-4 rounded-xl border border-gold/25 bg-charcoal p-3">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} autoFocus placeholder="e.g. Our standard cleaning rate is £25/hour."
            className="w-full resize-none bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none" data-testid="memory-input" />
          <div className="mt-2 flex items-center justify-between gap-2">
            <select value={draftScope} onChange={(e) => setDraftScope(e.target.value)} className="rounded-lg border border-white/10 bg-graphite px-3 py-1.5 text-xs text-white/70 focus:outline-none" data-testid="memory-scope-select">
              {!isGroupView && activeCompanyId && <option value="company">This business · {activeCompany?.name}</option>}
              <option value="group">Group (whole group)</option>
              <option value="private">Private (only me)</option>
              <option value="shared">Shared across businesses</option>
              {companies.map((c) => <option key={c.id} value={`company:${c.id}`}>Company · {c.name}</option>)}
            </select>
            <div className="flex gap-2">
              <button onClick={() => setAdding(false)} className="rounded-lg px-3 py-1.5 text-sm text-white/50 hover:text-white">Cancel</button>
              <button onClick={add} className={goldBtn} data-testid="memory-save">Save</button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-center gap-3 rounded-xl border border-white/10 bg-graphite px-4 py-2.5">
        <SearchIcon className="h-4 w-4 text-white/30" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search memories…" data-testid="memory-search"
          className="w-full bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none" />
      </div>

      <div className="mb-5 flex flex-wrap gap-2" data-testid="memory-filters">
        {["all", "group", "company", "shared", "private", "unassigned"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${filter === f ? "border-gold/40 bg-gold/10 text-gold" : "border-white/10 text-white/50 hover:text-white"}`} data-testid={`memory-filter-${f}`}>
            {f === "unassigned" ? "Needs review" : f}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={Brain} title="No memories here" hint="Add facts, or tell BDVG AI to 'remember' something in chat — it saves against the business you're in." />
      ) : (
        <div className="space-y-2">
          {shown.map((m) => {
            const meta = SCOPE_META[m.scope] || SCOPE_META.unassigned;
            const MetaIcon = meta.icon;
            return (
              <div key={m.id} className={`rounded-xl border bg-charcoal px-4 py-3 transition-colors ${m.enabled ? "border-white/8" : "border-white/5 opacity-50"}`} data-testid={`memory-${m.id}`}>
                <div className="flex items-start justify-between gap-3">
                  {editId === m.id ? (
                    <input value={editText} onChange={(e) => setEditText(e.target.value)} className="flex-1 rounded-lg bg-graphite px-3 py-1.5 text-sm focus:outline-none" data-testid="memory-edit-input" />
                  ) : (
                    <p className="flex-1 text-sm text-white/85">{m.content}</p>
                  )}
                  <div className="flex shrink-0 items-center gap-1">
                    {editId === m.id ? (
                      <>
                        <button onClick={() => saveEdit(m.id)} className="p-1.5 text-gold" data-testid="memory-edit-save"><Check className="h-4 w-4" /></button>
                        <button onClick={() => setEditId(null)} className="p-1.5 text-white/40"><X className="h-4 w-4" /></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => toggle(m)} className="text-xs text-white/40 hover:text-gold" data-testid={`memory-toggle-${m.id}`}>{m.enabled ? "Disable" : "Enable"}</button>
                        <button onClick={() => { setEditId(m.id); setEditText(m.content); }} className="p-1.5 text-white/40 hover:text-white"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => del(m.id)} className="p-1.5 text-white/40 hover:text-destructive" data-testid={`memory-delete-${m.id}`}><Trash2 className="h-4 w-4" /></button>
                      </>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${meta.cls}`} data-testid={`memory-scope-${m.id}`}>
                    <MetaIcon className="h-3 w-3" /> {meta.label}{m.scope === "company" && m.company_id ? ` · ${companyName(m.company_id)}` : ""}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-white/25">Source: {m.source}</span>
                  <select value={currentScopeVal(m)} onChange={(e) => reclassify(m, e.target.value)} className="ml-auto rounded-lg border border-white/10 bg-graphite px-2 py-1 text-[11px] text-white/60 focus:outline-none" data-testid={`memory-reclassify-${m.id}`}>
                    {scopeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    {(!m.scope || m.scope === "unassigned") && <option value="unassigned">Needs review</option>}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Scroller>
  );
}
